/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { observer } from 'mobx-react-lite'
import { useEffect, useLayoutEffect, useState } from 'react'
import Select from 'react-select'
import InputMask from 'react-input-mask'

import { useMobxStore } from '../../../state'
import { EventCard } from './ConfirmProfile'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import colors from '@sportninja/common/constants/appColors'
import Icon from '../../../components/Icon'

const DropdownIndicator = ({ onClick, ...props }) => {
  return (
    <div
      onClick={onClick}
      css={css`
        width: 30px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      `}
      {...props.innerProps}
    >
      <Icon
        name='chevron-down'
        color='#898a8a'
        hoverColor={colors.SOFT_STEEL}
      />
    </div>
  )
}

const TeamControl = ({ onClick, ...props }) => {
  const value = props.hasValue
    ? props.getValue()[0]
    : { name: 'Select...', image: null }

  return (
    <div
      onClick={onClick}
      css={css`
        display: flex;
        align-items: center;
        padding: 8px;
        flex: 1;
      `}
    >
      {value?.image?.full_path && (
        <img
          src={value?.image?.full_path}
          css={css`
            width: 24px;
            height: 24px;
            margin-right: 8px;
          `}
        />
      )}
      <span
        css={css`
          font-size: 16px;
          opacity: ${value.name === 'Select...' ? 0.5 : 1};
        `}
      >
        {value.name}
      </span>
    </div>
  )
}

const TeamOptions = ({ onClick, innerRef, innerProps, data }) => {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      onClick={() => {
        onClick()
        innerProps.onClick()
      }}
      css={css`
        display: flex;
        align-items: center;
        padding: 12px 8px;
        cursor: pointer;
        background-color: transparent;
        transition: all 0.2s ease-in-out;

        &:hover {
          background-color: white;
        }
      `}
    >
      {data?.image?.full_path && (
        <img
          src={data?.image?.full_path}
          css={css`
            width: 24px;
            height: 24px;
            margin-right: 8px;
          `}
        />
      )}
      <span
        css={css`
          font-size: 16px;
        `}
      >
        {data.name}
      </span>
    </div>
  )
}

export const Input = ({
  label,
  value,
  useAsRow,
  onChange,
  placeholder = '',
  inputType = undefined,
  maxLength = undefined,
  inputMaxWidth = undefined,
}) => (
  <div
    css={css`
      margin: ${!label ? '0' : '16px 0'};
    `}
  >
    {label && (
      <div
        css={css`
          margin-bottom: 8px;
        `}
      >
        <span
          css={css`
            font-size: 14px;
          `}
        >
          {label}
        </span>
      </div>
    )}
    {useAsRow ? (
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          padding: 8px;
          flex: 1;
          height: 40px;
          background-color: ${colors.SOLITUDE};
          border: 1px solid ${colors.ATTENDANCE_GRAY};
          border-radius: 4px;
        `}
      >
        <span
          css={css`
            font-size: 16px;
          `}
        >
          {value}
        </span>
      </div>
    ) : (
      <div
        css={css`
          flex: 1;
          display: flex;
        `}
      >
        <input
          type={inputType}
          placeholder={placeholder}
          maxLength={maxLength}
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 8px;
            flex: 1;
            height: 40px;
            background-color: ${colors.SOLITUDE};
            border: 1px solid ${colors.ATTENDANCE_GRAY};
            border-radius: 4px;
            font-size: 16px;
            max-width: ${inputMaxWidth || '100%'};
          `}
          value={value}
          onChange={onChange}
        />
      </div>
    )}
  </div>
)

const SelectControl = ({ onClick, ...props }) => {
  const value = props.hasValue ? props.getValue()[0] : { name: 'Select...' }

  return (
    <div
      onClick={onClick}
      css={css`
        align-items: center;
        padding: 8px;
        display: flex;
        flex: 1;
      `}
    >
      <span
        css={css`
          font-size: 16px;
          opacity: ${value.name === 'Select...' ? 0.5 : 1};
        `}
      >
        {value.name || String(value.tier).split(' ')[0]}
      </span>
    </div>
  )
}

const SelectOptions = ({ onClick, innerRef, innerProps, data }) => {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      onClick={() => {
        onClick()
        innerProps.onClick()
      }}
      css={css`
        padding: 12px 8px;
        cursor: pointer;
        background-color: transparent;
        transition: all 0.2s ease-in-out;

        &:hover {
          background-color: white;
        }
      `}
    >
      <span
        css={css`
          font-size: 16px;
          margin-left: 8px;
        `}
      >
        {data.name || data.tier}
      </span>
    </div>
  )
}

export const NewPlayerRegistration = observer(() => {
  const {
    registrationInvite: {
      state: {
        eventInfo,
        userExtraInfo,
        playerTeams,
        skillLevels,
        selectedTeamPlayerRosterId,
      },
      confirmPlayerSelect,
      pushToStep,
    },
  } = useMobxStore()

  const [selectedTeam, setSelectedTeam] = useState({})
  const [number, setPlayerNumber] = useState('')
  const [position, setPlayerPosition] = useState(null)
  const [dateOfBirthday, setPlayerDateOfBirthday] = useState({
    year: '',
    month: '',
    day: '',
  })
  const [skillLevel, setPlayerSkillLevel] = useState(undefined)

  const [teamMenuIsOpen, setTeamMenuIsOpen] = useState(false)
  const [positionMenuIsOpen, setPositionMenuIsOpen] = useState(false)
  const [skillMenuIsOpen, setSkillMenuIsOpen] = useState(false)
  const [disabled, setDisabled] = useState(true)
  const [preselectedTeam, setPreselectedTeam] = useState(null)

  useEffect(() => {
    if (
      (selectedTeam?.id || preselectedTeam?.id) &&
      number !== '' &&
      position &&
      dateOfBirthday.day !== '' &&
      dateOfBirthday.month !== '' &&
      dateOfBirthday.year !== '' &&
      skillLevel
    ) {
      setDisabled(false)
    } else {
      setDisabled(true)
    }
  }, [selectedTeam, number, position, dateOfBirthday, skillLevel])

  useLayoutEffect(() => {
    if (selectedTeamPlayerRosterId) {
      const team = playerTeams?.find(
        (team) => team.team_player_roster_id === selectedTeamPlayerRosterId
      )

      if (team) {
        setPreselectedTeam(team)
      }
    }
  }, [selectedTeamPlayerRosterId])

  return (
    <div>
      <button
        onClick={() => pushToStep(0)}
        css={css`
          padding-top: 16px;
          align-items: center;
          display: flex;
          gap: 8px;
        `}
      >
        <Icon name='arrow-left' color={colors.DEFAULT_FLAIR} />
        <p
          css={css`
            color: ${colors.DEFAULT_FLAIR};
            font-weight: 700;
          `}
        >
          Go Back
        </p>
      </button>
      <EventCard eventInfo={eventInfo} />
      <h1
        css={css`
          text-align: left;
          margin-top: 32px;
          font-size: 34px;
          font-weight: 700;
          font-family: ${isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
            : 'Rift, Arial, Helvetica, sans-serif'};
        `}
      >
        NEW PLAYER REGISTRATION
      </h1>
      <p
        css={css`
          font-size: 16px;
          margin: 32px 0;
        `}
      >
        PLAYER CONTACT INFO
      </p>
      <Input
        label='Name'
        useAsRow
        value={userExtraInfo?.name_first + ' ' + userExtraInfo?.name_last}
      />
      <Input label='Email Address' value={userExtraInfo?.email} useAsRow />
      {userExtraInfo?.phone && (
        <Input label='Phone' value={userExtraInfo?.phone} useAsRow />
      )}

      <div
        css={css`
          display: flex;
          align-items: center;
          width: 100%;
        `}
      >
        <div
          css={css`
            flex: 1;
            margin-right: 8px;
          `}
        >
          <Input
            value={number}
            placeholder='99'
            inputType='number'
            onChange={(e) => setPlayerNumber(e.target.value)}
            label='Number'
          />
        </div>

        <div
          css={css`
            flex: 1;
            margin-left: 8px;
          `}
        >
          <p
            css={css`
              font-size: 14px;
              margin-bottom: 8px;
            `}
          >
            Desired Position
          </p>

          <Select
            options={[
              { id: 3, name: 'Forward' },
              { id: 2, name: 'Defense' },
              { id: 1, name: 'Goalie' },
              { id: 4, name: 'Skater' },
            ]}
            name='position_selector'
            menuIsOpen={positionMenuIsOpen}
            onChange={(value) => {
              setPlayerPosition(value.id)
            }}
            components={{
              Option: (props) => (
                <SelectOptions
                  {...props}
                  onClick={() => setPositionMenuIsOpen(!positionMenuIsOpen)}
                />
              ),
              DropdownIndicator: (props) => (
                <DropdownIndicator
                  {...props}
                  onClick={() => setPositionMenuIsOpen(!positionMenuIsOpen)}
                />
              ),
              ValueContainer: (props) => (
                <SelectControl
                  {...props}
                  onClick={() => setPositionMenuIsOpen(!positionMenuIsOpen)}
                />
              ),
            }}
            styles={{
              container: (baseStyles) => ({
                ...baseStyles,
                width: '100%',
              }),
              control: (baseStyles) => ({
                ...baseStyles,
                backgroundColor: colors.SOLITUDE,
                borderColor: colors.ATTENDANCE_GRAY,
                minHeight: 40,
              }),
              menu: (baseStyles) => ({
                ...baseStyles,
                backgroundColor: colors.SOLITUDE,
                borderColor: colors.ATTENDANCE_GRAY,
              }),
              option: (baseStyles, { isFocused }) => ({
                ...baseStyles,
                backgroundColor: isFocused
                  ? 'rgba(255, 255, 255, 0.9)'
                  : undefined,
              }),
            }}
          />
        </div>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          width: 100%;
        `}
      >
        <div
          css={css`
            flex: 1;
            margin-right: 8px;
          `}
        >
          <p
            css={css`
              font-size: 14px;
              margin-bottom: 8px;
            `}
          >
            Date of Birthday
          </p>
          <div
            css={css`
              display: flex;
              gap: 4px;
              align-items: center;
              flex: 1;
            `}
          >
            <Input
              value={dateOfBirthday.year}
              placeholder='year'
              inputType='number'
              inputMaxWidth='94px'
              onChange={(e) =>
                setPlayerDateOfBirthday({
                  ...dateOfBirthday,
                  year: e.target.value,
                })
              }
            />
            <Input
              value={dateOfBirthday.month}
              placeholder='month'
              inputType='number'
              inputMaxWidth='94px'
              onChange={(e) =>
                setPlayerDateOfBirthday({
                  ...dateOfBirthday,
                  month: e.target.value,
                })
              }
            />
            <Input
              value={dateOfBirthday.day}
              placeholder='day'
              inputType='number'
              inputMaxWidth='94px'
              onChange={(e) =>
                setPlayerDateOfBirthday({
                  ...dateOfBirthday,
                  day: e.target.value,
                })
              }
            />
          </div>
        </div>

        <div
          css={css`
            flex: 1;
            margin-left: 8px;
          `}
        >
          <p
            css={css`
              font-size: 14px;
              margin-bottom: 8px;
            `}
          >
            Skill Level
          </p>

          <Select
            options={skillLevels ?? []}
            name='skill_selector'
            menuIsOpen={skillMenuIsOpen}
            onChange={(value) => {
              setPlayerSkillLevel(value.id)
            }}
            components={{
              Option: (props) => (
                <SelectOptions
                  {...props}
                  onClick={() => setSkillMenuIsOpen(!skillMenuIsOpen)}
                />
              ),
              DropdownIndicator: (props) => (
                <DropdownIndicator
                  {...props}
                  onClick={() => setSkillMenuIsOpen(!skillMenuIsOpen)}
                />
              ),
              ValueContainer: (props) => (
                <SelectControl
                  {...props}
                  onClick={() => setSkillMenuIsOpen(!skillMenuIsOpen)}
                />
              ),
            }}
            styles={{
              container: (baseStyles) => ({
                ...baseStyles,
                width: '100%',
              }),
              control: (baseStyles) => ({
                ...baseStyles,
                backgroundColor: colors.SOLITUDE,
                borderColor: colors.ATTENDANCE_GRAY,
                minHeight: 40,
              }),
              menu: (baseStyles) => ({
                ...baseStyles,
                backgroundColor: colors.SOLITUDE,
                borderColor: colors.ATTENDANCE_GRAY,
              }),
              option: (baseStyles, { isFocused }) => ({
                ...baseStyles,
                backgroundColor: isFocused
                  ? 'rgba(255, 255, 255, 0.9)'
                  : undefined,
              }),
            }}
          />
        </div>
      </div>

      <div
        css={css`
          margin-bottom: 50px;
        `}
      >
        <p
          css={css`
            font-size: 16px;
            margin: 32px 0;
          `}
        >
          TEAM INFO
        </p>

        {preselectedTeam ? (
          <div
            css={css`
              background-color: ${colors.LINK_WATER};
              padding: 16px;
              border-radius: 8px;
              margin: 20px 0;
              width: 100%;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              {preselectedTeam.image?.full_path && (
                <img
                  src={preselectedTeam.image?.full_path}
                  css={css`
                    width: 36px;
                    height: 36px;
                    margin-right: 8px;
                  `}
                />
              )}
              {preselectedTeam?.name && (
                <p
                  css={css`
                    font-family: ${isCanlan
                      ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                      : 'Rift, Arial, Helvetica, sans-serif'};
                    font-weight: 700;
                    font-size: 36px;
                    color: #27303e;
                  `}
                >
                  {preselectedTeam?.name}
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            <p
              css={css`
                font-size: 14px;
                margin-bottom: 8px;
              `}
            >
              Team Name
            </p>
            <Select
              options={playerTeams ?? []}
              name='team_selector'
              menuIsOpen={teamMenuIsOpen}
              onChange={(e) => e?.id && setSelectedTeam(e)}
              components={{
                Option: (props) => (
                  <TeamOptions
                    {...props}
                    onClick={() => setTeamMenuIsOpen(!teamMenuIsOpen)}
                  />
                ),
                DropdownIndicator: (props) => (
                  <DropdownIndicator
                    {...props}
                    onClick={() => setTeamMenuIsOpen(!teamMenuIsOpen)}
                  />
                ),
                ValueContainer: (props) => (
                  <TeamControl
                    {...props}
                    onClick={() => setTeamMenuIsOpen(!teamMenuIsOpen)}
                  />
                ),
              }}
              styles={{
                container: (baseStyles) => ({
                  ...baseStyles,
                  width: '100%',
                }),
                control: (baseStyles) => ({
                  ...baseStyles,
                  backgroundColor: colors.SOLITUDE,
                  borderColor: colors.ATTENDANCE_GRAY,
                  minHeight: 40,
                }),
                menu: (baseStyles) => ({
                  ...baseStyles,
                  backgroundColor: colors.SOLITUDE,
                  borderColor: colors.ATTENDANCE_GRAY,
                }),
                option: (baseStyles, { isFocused }) => ({
                  ...baseStyles,
                  backgroundColor: isFocused
                    ? 'rgba(255, 255, 255, 0.9)'
                    : undefined,
                }),
              }}
            />
          </>
        )}
      </div>

      <button
        disabled={disabled}
        onClick={() =>
          confirmPlayerSelect({
            endpointInfo: {
              rosterId:
                selectedTeam?.team_player_roster_id ||
                preselectedTeam?.team_player_roster_id,
              scheduleId: eventInfo?.id,
              teamId: selectedTeam?.id || preselectedTeam?.id,
              teamImageUrl:
                selectedTeam?.image?.full_path ||
                preselectedTeam?.image?.full_path,
              teamName: selectedTeam?.name || preselectedTeam?.name,
            },
            playerInfo: {
              firstName: userExtraInfo?.name_first,
              lastName: userExtraInfo?.name_last,
              email: userExtraInfo?.email,
              dateOfBirthday: `${dateOfBirthday.year}-${dateOfBirthday.month}-${dateOfBirthday.day}`,
              skillLevel: skillLevel,
              number: number,
              position: position,
            },
          })
        }
        css={css`
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
          display: flex;
          opacity: ${disabled ? 0.5 : 1};
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          height: 40px;
          border-radius: 8px;
          background-color: ${colors.DEFAULT_FLAIR};
          color: ${colors.WHITE};
          font-size: 18px;
          font-weight: 700;
          font-family: ${isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
            : 'Rift, Arial, Helvetica, sans-serif'};
          transition: all 0.2s ease-in-out;

          &:hover {
            ${!disabled && `background-color: ${colors.DEFAULT_FLAIR_HOVER};`}
          }
        `}
      >
        CONFIRM MY REGISTRATION
      </button>
    </div>
  )
})
